import { useEffect, useState, type FormEvent } from 'react';
import type { Session } from '@supabase/supabase-js';
import App from './App';
import { supabase } from './lib/supabase';
import './auth.css';

type AccountAccess = {
  id: string;
  display_name: string;
  account_type: 'household_member' | 'creditor_viewer';
};

type CreditorSummary = {
  mortgageBalance: number;
  principalPrepayments: number;
  remainderPending: number;
  debtOriginal: number;
  debtBalance: number;
  debtPaid: number;
  prepaymentCount: number;
  lastMortgageDate: string | null;
};

const currency = new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'MXN',
  maximumFractionDigits: 2,
});

const dateFormatter = new Intl.DateTimeFormat('es-MX', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});

function authMessage(message: string) {
  const normalized = message.toLowerCase();

  if (normalized.includes('no está autorizado') || normalized.includes('not authorized')) {
    return 'Este correo no está autorizado para Finanzas Hogar.';
  }

  if (normalized.includes('invalid login credentials')) {
    return 'Correo o contraseña incorrectos.';
  }

  if (normalized.includes('already registered')) {
    return 'Este correo ya tiene una cuenta. Inicia sesión o recupera tu contraseña.';
  }

  return message;
}

function LoginScreen() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const normalizedEmail = email.trim().toLowerCase();

  async function signIn(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage('');

    const { error } = await supabase.auth.signInWithPassword({
      email: normalizedEmail,
      password,
    });

    setMessage(error ? authMessage(error.message) : 'Acceso correcto.');
    setBusy(false);
  }

  async function signUp() {
    setBusy(true);
    setMessage('');

    const { error } = await supabase.auth.signUp({
      email: normalizedEmail,
      password,
      options: { emailRedirectTo: window.location.origin },
    });

    setMessage(
      error
        ? authMessage(error.message)
        : 'Cuenta creada. Revisa tu correo para confirmar el acceso.',
    );
    setBusy(false);
  }

  async function sendMagicLink() {
    setBusy(true);
    setMessage('');

    const { error } = await supabase.auth.signInWithOtp({
      email: normalizedEmail,
      options: {
        emailRedirectTo: window.location.origin,
        shouldCreateUser: true,
      },
    });

    setMessage(error ? authMessage(error.message) : 'Enlace enviado. Revisa tu correo.');
    setBusy(false);
  }

  async function resetPassword() {
    if (!normalizedEmail) {
      setMessage('Escribe tu correo para solicitar la recuperación.');
      return;
    }

    setBusy(true);
    setMessage('');

    const { error } = await supabase.auth.resetPasswordForEmail(normalizedEmail, {
      redirectTo: window.location.origin,
    });

    setMessage(error ? authMessage(error.message) : 'Se envió un enlace de recuperación.');
    setBusy(false);
  }

  return (
    <main className="auth-page">
      <section className="auth-hero">
        <span className="auth-brand-mark">FH</span>
        <p className="auth-eyebrow">Finanzas Hogar</p>
        <h1>La vivienda y sus compromisos, en un solo lugar.</h1>
        <p>Acceso privado para Juan Carlos, Ximena y el portal acreedor.</p>
        <div className="auth-feature-grid">
          <span>Hipoteca</span><span>Remanentes</span><span>Servicios</span><span>Planeación</span>
        </div>
      </section>

      <section className="auth-card">
        <div>
          <p className="auth-eyebrow">Acceso privado</p>
          <h2>Iniciar sesión</h2>
          <p>Usa exclusivamente el correo autorizado para tu perfil.</p>
        </div>
        <form onSubmit={signIn}>
          <label>
            Correo electrónico
            <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} required autoComplete="email" />
          </label>
          <label>
            Contraseña
            <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} required minLength={8} autoComplete="current-password" />
          </label>
          <button className="auth-primary" type="submit" disabled={busy}>{busy ? 'Procesando…' : 'Entrar'}</button>
        </form>
        <div className="auth-actions">
          <button type="button" onClick={signUp} disabled={busy || !normalizedEmail || password.length < 8}>Crear acceso inicial</button>
          <button type="button" onClick={sendMagicLink} disabled={busy || !normalizedEmail}>Enviar enlace por correo</button>
          <button type="button" onClick={resetPassword} disabled={busy || !normalizedEmail}>Recuperar contraseña</button>
        </div>
        {message && <p className="auth-message" role="status">{message}</p>}
        <small>Los correos no autorizados son rechazados durante la creación de la cuenta.</small>
      </section>
    </main>
  );
}

async function loadCreditorSummary(): Promise<CreditorSummary> {
  const [mortgageResult, prepaymentsResult, remaindersResult, debtResult, debtPaymentsResult, installmentsResult] = await Promise.all([
    supabase.from('mortgage_accounts').select('current_projected_balance').limit(1).maybeSingle(),
    supabase.from('principal_prepayments_public').select('amount'),
    supabase.from('reimbursement_remainders_public').select('remainder_amount, settled_amount, status'),
    supabase.from('acquisition_debts_public').select('original_amount, current_balance').limit(1).maybeSingle(),
    supabase.from('acquisition_debt_payments_public').select('amount'),
    supabase.from('mortgage_installments_public').select('due_date').order('due_date', { ascending: false }).limit(1).maybeSingle(),
  ]);

  const firstError = [
    mortgageResult.error,
    prepaymentsResult.error,
    remaindersResult.error,
    debtResult.error,
    debtPaymentsResult.error,
    installmentsResult.error,
  ].find(Boolean);

  if (firstError) throw firstError;

  const principalPrepayments = (prepaymentsResult.data ?? []).reduce(
    (total, row) => total + Number(row.amount ?? 0),
    0,
  );

  const remainderPending = (remaindersResult.data ?? [])
    .filter((row) => row.status !== 'settled' && row.status !== 'cancelled')
    .reduce(
      (total, row) => total + Number(row.remainder_amount ?? 0) - Number(row.settled_amount ?? 0),
      0,
    );

  const debtPaid = (debtPaymentsResult.data ?? []).reduce(
    (total, row) => total + Number(row.amount ?? 0),
    0,
  );

  return {
    mortgageBalance: Number(mortgageResult.data?.current_projected_balance ?? 0),
    principalPrepayments,
    remainderPending,
    debtOriginal: Number(debtResult.data?.original_amount ?? 0),
    debtBalance: Number(debtResult.data?.current_balance ?? 0),
    debtPaid,
    prepaymentCount: prepaymentsResult.data?.length ?? 0,
    lastMortgageDate: installmentsResult.data?.due_date ?? null,
  };
}

function CreditorPortal({ displayName, onSignOut }: { displayName: string; onSignOut: () => void }) {
  const [summary, setSummary] = useState<CreditorSummary | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    void loadCreditorSummary()
      .then((data) => {
        if (active) setSummary(data);
      })
      .catch(() => {
        if (active) setError('No fue posible cargar la información financiera. Intenta nuevamente.');
      });

    return () => {
      active = false;
    };
  }, []);

  return (
    <main className="creditor-page">
      <header className="creditor-header">
        <div><span className="auth-brand-mark">FH</span><div><strong>Portal acreedor</strong><small>Solo lectura</small></div></div>
        <div><span>{displayName}</span><button type="button" onClick={onSignOut}>Cerrar sesión</button></div>
      </header>
      <section className="creditor-heading">
        <p>Seguimiento autorizado</p>
        <h1>Información que te corresponde.</h1>
        <span>No se muestran rentas, aportaciones individuales, servicios ni cuenta conjunta.</span>
      </section>

      {!summary && !error && <section className="creditor-loading">Cargando cifras conciliadas…</section>}
      {error && <section className="creditor-error">{error}</section>}

      {summary && (
        <>
          <section className="creditor-cards">
            <article><small>Enganche + notaría pendiente</small><strong>{currency.format(summary.debtBalance)}</strong><span>{currency.format(summary.debtPaid)} pagados de {currency.format(summary.debtOriginal)}</span></article>
            <article><small>Remanente pendiente</small><strong>{currency.format(summary.remainderPending)}</strong><span>Acumulado por liquidar</span></article>
            <article><small>Saldo hipotecario</small><strong>{currency.format(summary.mortgageBalance)}</strong><span>Última proyección disponible</span></article>
            <article><small>Abonos a capital</small><strong>{currency.format(summary.principalPrepayments)}</strong><span>{summary.prepaymentCount} movimientos confirmados</span></article>
          </section>
          <section className="creditor-detail">
            <article><h2>Deuda de enganche y notaría</h2><div><span>Importe original</span><strong>{currency.format(summary.debtOriginal)}</strong></div><div><span>Total pagado</span><strong>{currency.format(summary.debtPaid)}</strong></div><div><span>Saldo pendiente</span><strong>{currency.format(summary.debtBalance)}</strong></div></article>
            <article><h2>Actividad autorizada</h2><div><span>Abonos a capital</span><strong>{currency.format(summary.principalPrepayments)}</strong></div><div><span>Remanente acumulado</span><strong>{currency.format(summary.remainderPending)}</strong></div><div><span>Último periodo bancario</span><strong>{summary.lastMortgageDate ? dateFormatter.format(new Date(`${summary.lastMortgageDate}T00:00:00Z`)) : 'Pendiente'}</strong></div></article>
          </section>
        </>
      )}
    </main>
  );
}

function HouseholdShell({
  displayName,
  userId,
  userEmail,
  onSignOut,
}: {
  displayName: string;
  userId: string;
  userEmail: string;
  onSignOut: () => void;
}) {
  return (
    <>
      <div className="session-bar"><span>Sesión: <strong>{displayName}</strong></span><button type="button" onClick={onSignOut}>Cerrar sesión</button></div>
      <App userId={userId} userEmail={userEmail} displayName={displayName} />
    </>
  );
}

export default function Root() {
  const [session, setSession] = useState<Session | null>(null);
  const [access, setAccess] = useState<AccountAccess | null>(null);
  const [checking, setChecking] = useState(true);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    void supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => setSession(nextSession));
    return () => data.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    async function loadAccess() {
      if (!session) {
        setAccess(null);
        setDenied(false);
        setChecking(false);
        return;
      }

      setChecking(true);
      const { data, error } = await supabase
        .from('profiles')
        .select('id, display_name, account_type')
        .eq('id', session.user.id)
        .maybeSingle();

      if (error || !data) {
        setDenied(true);
        setAccess(null);
        await supabase.auth.signOut();
      } else {
        setAccess(data as AccountAccess);
        setDenied(false);
      }
      setChecking(false);
    }

    void loadAccess();
  }, [session]);

  async function signOut() {
    await supabase.auth.signOut();
  }

  if (checking) return <main className="auth-loading">Verificando acceso…</main>;
  if (!session || !access) return <><LoginScreen />{denied && <div className="auth-denied">Este correo no está autorizado para Finanzas Hogar.</div>}</>;
  if (access.account_type === 'creditor_viewer') return <CreditorPortal displayName={access.display_name} onSignOut={() => void signOut()} />;
  return (
    <HouseholdShell
      displayName={access.display_name}
      userId={session.user.id}
      userEmail={session.user.email ?? ''}
      onSignOut={() => void signOut()}
    />
  );
}
